/**
 * CaptionPreview - Interactive caption preview with video playback simulation
 * Shows how captions will look with the selected preset
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import CaptionRenderer from './CaptionRenderer';
import { CAPTION_PRESETS } from '../lib/captionPresets';

/**
 * Demo caption blocks for preview when no real captions available
 */
const DEMO_BLOCKS = [
    {
        id: 'demo_1',
        startTime: 0,
        endTime: 2,
        words: [
            { text: 'THIS', emphasis: false },
            { text: 'IS', emphasis: false },
            { text: 'HOW', emphasis: true },
        ],
        preset: 'karaoke',
        position: { x: 50, y: 80 }
    },
    {
        id: 'demo_2',
        startTime: 2,
        endTime: 4,
        words: [
            { text: 'YOUR', emphasis: false },
            { text: 'CAPTIONS', emphasis: true },
            { text: 'WILL', emphasis: false },
            { text: 'LOOK', emphasis: false },
        ],
        preset: 'karaoke',
        position: { x: 50, y: 80 }
    },
    {
        id: 'demo_3',
        startTime: 4,
        endTime: 6,
        words: [
            { text: 'CHOOSE', emphasis: false },
            { text: 'A', emphasis: false },
            { text: 'PRESET', emphasis: true },
        ],
        preset: 'karaoke',
        position: { x: 50, y: 80 }
    }
];

/**
 * CaptionPreview component
 * @param {Object} props
 * @param {Array} props.blocks - Caption blocks (uses demo if empty)
 * @param {string} props.presetId - Selected preset ID
 * @param {string} props.videoThumbnail - Background thumbnail URL
 * @param {boolean} props.autoPlay - Auto-play the preview animation
 */
const CaptionPreview = ({
    blocks = [],
    presetId = 'karaoke',
    videoThumbnail,
    autoPlay = true,
    position = 80,
    onPositionChange
}) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const animationRef = useRef(null);
    const lastTimeRef = useRef(performance.now());

    // Use demo blocks if no real blocks provided
    const displayBlocks = blocks.length > 0
        ? blocks.map(b => ({ ...b, preset: presetId, position: { x: 50, y: position } }))
        : DEMO_BLOCKS.map(b => ({ ...b, preset: presetId, position: { x: 50, y: position } }));

    // Get total duration
    const totalDuration = Math.max(...displayBlocks.map(b => b.endTime), 6);

    // Animation loop
    useEffect(() => {
        if (!isPlaying) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            return;
        }

        const animate = (timestamp) => {
            const delta = (timestamp - lastTimeRef.current) / 1000;
            lastTimeRef.current = timestamp;

            setCurrentTime(prev => {
                const next = prev + delta;
                // Loop back to start
                if (next >= totalDuration) {
                    return 0;
                }
                return next;
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        lastTimeRef.current = performance.now();
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isPlaying, totalDuration]);

    // Get preset for styling
    const preset = CAPTION_PRESETS[presetId] || CAPTION_PRESETS.karaoke;

    return (
        <div className="relative w-full h-full">
            {/* Background */}
            {videoThumbnail ? (
                <img
                    src={videoThumbnail}
                    alt="Preview"
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-b from-dark-400 to-dark-300" />
            )}

            {/* Caption Renderer */}
            <CaptionRenderer
                blocks={displayBlocks}
                currentTime={currentTime}
                globalPreset={presetId}
            />

            {/* Playback controls */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1">
                <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-6 h-6 flex items-center justify-center text-white hover:text-amber-400 transition"
                >
                    {isPlaying ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <polygon points="5,3 19,12 5,21" />
                        </svg>
                    )}
                </button>
                <div className="text-xs text-white/70 font-mono min-w-[40px]">
                    {currentTime.toFixed(1)}s
                </div>

                {/* Progress bar */}
                <div className="w-16 h-1 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-amber-400"
                        style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                    />
                </div>
            </div>

            {/* Preset badge */}
            <div className="absolute top-3 left-3 bg-amber-500/20 text-amber-400 text-xs px-2 py-1 rounded-full font-medium">
                {preset.name}
            </div>
        </div>
    );
};

export default CaptionPreview;
